import { useEffect, useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { startGoogleLogin } from '@/modules/auth/googleSso';
import {
  authButtonClickedEvents,
  type AuthIntent,
} from '@/modules/auth/types/externalLogin';
import { Button } from '@/modules/ui/components/button/Button';

import { GoogleLogo } from './GoogleLogo';

interface GoogleLoginProps {
  returnTo?: string;
  /** True once the Terms Notice beside the button shows its link. */
  acceptTerms: boolean;
  /** Picks the label, the click event and whether the callback reports `userSignedUp` or `userSignedIn`. */
  intent: AuthIntent;
  onError: (error?: Error) => void;
}

/**
 * Google's light "Sign in with Google" button as its script drew it: white,
 * a light grey border, the four-colour "G" at the left edge and a centred
 * label. It redirects to Google rather than opening a popup (see
 * `googleSso.ts`); `/auth/callback/google` finishes the sign-in.
 */
export const GoogleLogin = ({
  returnTo,
  acceptTerms,
  intent,
  onError,
}: GoogleLoginProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const trackEvent = useTrackEvent();

  // Back from Google, Safari restores this page from its back/forward cache
  // with the spinner still on.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsLoading(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleLogin = () => {
    try {
      setIsLoading(true);
      trackEvent(authButtonClickedEvents[intent], { authMethod: 'Google' });
      trackEvent('googleSSO', { stage: 'started' });

      startGoogleLogin(returnTo, acceptTerms, intent);
    } catch (error) {
      setIsLoading(false);
      onError(error instanceof Error ? error : undefined);
    }
  };

  return (
    <Button
      variant="outline"
      loading={isLoading}
      onClick={handleLogin}
      className="relative h-10 w-full rounded-[4px] border-[#dadce0] font-medium text-[#1f1f1f] active:scale-[0.98]"
    >
      {!isLoading && <GoogleLogo className="absolute left-3" />}
      {intent === 'signUp' ? 'Sign up with Google' : 'Continue with Google'}
    </Button>
  );
};
